import {
	AutoFixHighOutlined,
	FormatListBulletedOutlined,
	FormatListNumberedOutlined,
	FormatQuoteOutlined,
	HorizontalRuleOutlined,
	ImageOutlined,
	NumbersOutlined,
	TableChartOutlined,
	TextFieldsOutlined,
} from '@mui/icons-material';
import type { SvgIcon } from '@mui/material';
import { type Editor, Extension } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { ReactRenderer } from '@tiptap/react';
import Suggestion, { type SuggestionKeyDownProps, type SuggestionProps } from '@tiptap/suggestion';
import i18n from 'i18next';
import tippy from 'tippy.js';

import MenuList from '../../components/RichTextEditor/MenuList';

const extensionName = 'slashCommand';

export interface Command {
	name: string;
	label: string;
	icon: typeof SvgIcon;
	action: (
		editor: Editor,
		options: {
			ai: {
				openGenerateWorksheetModal: () => void;
				openTableInsertDialog: () => void;
				openImageUploadDialog: () => void;
			};
			mathEditor?: {
				onInlineMathClick: (node: PMNode, pos: number) => void;
				onBlockMathClick: (node: PMNode, pos: number) => void;
			};
		}
	) => void;
	isEnabled: boolean;
	aliases?: string[];
}

export interface MenuOption {
	name: string;
	title: string;
	commands: Command[];
	isEnabled: boolean;
}

interface SlashCommandExtensionOption {
	ai: {
		openGenerateWorksheetModal: () => void;
		openTableInsertDialog: () => void;
		openImageUploadDialog: () => void;
	};
	disabledCommands?: string[];
	mathEditor?: {
		onInlineMathClick: (node: PMNode, pos: number) => void;
		onBlockMathClick: (node: PMNode, pos: number) => void;
	};
}

export const MENU_OPTIONS: MenuOption[] = [
	{
		name: 'insert',
		title: i18n.t('Insert'),
		isEnabled: true,
		commands: [
			{
				name: 'image',
				label: i18n.t('Image'),
				icon: ImageOutlined,
				aliases: ['img'],
				action: (_, { ai: { openImageUploadDialog } }) => {
					openImageUploadDialog();
				},
				isEnabled: true,
			},
			{
				name: 'horizontalRule',
				label: i18n.t('Horizontal Rule'),
				icon: HorizontalRuleOutlined,
				aliases: ['hr'],
				action: (editor) => {
					editor.chain().focus().setHorizontalRule().run();
				},
				isEnabled: true,
			},
			{
				name: 'ai',
				label: i18n.t('AI worksheet'),
				icon: AutoFixHighOutlined,
				aliases: ['ai'],
				action: (_, { ai: { openGenerateWorksheetModal } }) => {
					openGenerateWorksheetModal();
				},
				isEnabled: true,
			},
			{
				name: 'table',
				label: i18n.t('Table'),
				icon: TableChartOutlined,
				aliases: ['table', 'tbl'],
				action: (_, { ai: { openTableInsertDialog } }) => {
					openTableInsertDialog();
				},
				isEnabled: true,
			},
			{
				name: 'math',
				label: i18n.t('Formula'),
				icon: NumbersOutlined,
				action: (editor, { mathEditor }) => {
					if (!mathEditor) {
						return;
					}

					// The slash command text has already been deleted by the command handler.
					// Insert a block math with a placeholder space — TipTap/ProseMirror
					// requires non-empty attrs for the node to be valid and selectable.
					const success = editor.chain().insertBlockMath({ latex: ' ' }).run();

					if (!success) {
						return;
					}

					// Use requestAnimationFrame to reliably wait for the DOM to update
					// after the ProseMirror transaction, instead of a fragile setTimeout.
					requestAnimationFrame(() => {
						const pos = editor.state.selection.$from.pos;
						const $pos = editor.state.doc.resolve(pos);

						let mathNode: PMNode | null = null;
						let mathPos = -1;

						// Check node before cursor
						if ($pos.nodeBefore && $pos.nodeBefore.type.name === 'blockMath') {
							mathNode = $pos.nodeBefore;
							mathPos = pos - mathNode.nodeSize;
						} else {
							// Check at current position
							const nodeAt = editor.state.doc.nodeAt(pos);
							if (nodeAt && nodeAt.type.name === 'blockMath') {
								mathNode = nodeAt;
								mathPos = pos;
							}
						}

						if (mathNode && mathPos >= 0) {
							mathEditor.onBlockMathClick(mathNode, mathPos);
						}
					});
				},
				isEnabled: true,
			},
		],
	},
	{
		name: 'format',
		title: i18n.t('Format'),
		isEnabled: true,
		commands: [
			{
				name: 'heading1',
				label: `${i18n.t('Heading')} 1`,
				icon: TextFieldsOutlined,
				aliases: ['h1'],
				action: (editor) => {
					editor.chain().focus().setHeading({ level: 1 }).run();
				},
				isEnabled: true,
			},
			{
				name: 'heading2',
				label: `${i18n.t('Heading')} 2`,
				icon: TextFieldsOutlined,
				aliases: ['h2'],
				action: (editor) => {
					editor.chain().focus().setHeading({ level: 2 }).run();
				},
				isEnabled: true,
			},
			{
				name: 'heading3',
				label: `${i18n.t('Heading')} 3`,
				icon: TextFieldsOutlined,
				aliases: ['h3'],
				action: (editor) => {
					editor.chain().focus().setHeading({ level: 3 }).run();
				},
				isEnabled: true,
			},
			{
				name: 'blockquote',
				label: i18n.t('Blockquote'),
				icon: FormatQuoteOutlined,
				action: (editor) => {
					editor.chain().focus().setBlockquote().run();
				},
				isEnabled: true,
			},
			{
				name: 'bulletList',
				label: i18n.t('Bullet List'),
				icon: FormatListBulletedOutlined,
				aliases: ['ul'],
				action: (editor) => {
					editor.chain().focus().toggleBulletList().run();
				},
				isEnabled: true,
			},
			{
				name: 'numberedList',
				label: i18n.t('Numbered List'),
				icon: FormatListNumberedOutlined,
				aliases: ['ol'],
				action: (editor) => {
					editor.chain().focus().toggleOrderedList().run();
				},
				isEnabled: true,
			},
		],
	},
];

let popup: any;

export const SlashCommand = Extension.create<SlashCommandExtensionOption>({
	name: extensionName,

	priority: 200,

	addOptions() {
		return {
			ai: {
				openGenerateWorksheetModal: () => {},
				openTableInsertDialog: () => {},
				openImageUploadDialog: () => {},
			},
			mathEditor: undefined,
			disabledCommands: [],
		};
	},

	onCreate() {
		popup = tippy('body', {
			interactive: true,
			trigger: 'manual',
			placement: 'bottom-start',
			theme: 'slash-command',
			maxWidth: '16rem',
			offset: [16, 8],
			popperOptions: {
				strategy: 'fixed',
				modifiers: [
					{
						name: 'flip',
						enabled: false,
					},
				],
			},
		});
	},

	addProseMirrorPlugins() {
		return [
			Suggestion({
				editor: this.editor,
				char: '/',
				allowSpaces: true,
				startOfLine: true,
				pluginKey: new PluginKey(extensionName),
				allow: ({ state, range }) => {
					const $from = state.doc.resolve(range.from);
					const isRootDepth = $from.depth === 1;
					const isParagraph = $from.parent.type.name === 'paragraph';
					const isStartOfNode = $from.parent.textContent?.charAt(0) === '/';
					const isInColumn = this.editor.isActive('column');

					const afterContent = $from.parent.textContent?.substring(
						$from.parent.textContent?.indexOf('/')
					);
					const isValidAfterContent = !afterContent?.endsWith('  ');

					return (
						((isRootDepth && isParagraph && isStartOfNode) ||
							(isInColumn && isParagraph && isStartOfNode)) &&
						isValidAfterContent
					);
				},
				command: ({ editor, props }: { editor: Editor; props: any }) => {
					const { view, state } = editor;
					const { $head, $from } = view.state.selection;

					const end = $from.pos;
					const from = $head?.nodeBefore
						? end - ($head.nodeBefore.text?.substring($head.nodeBefore.text?.indexOf('/')).length ?? 0)
						: $from.start();

					const tr = state.tr.deleteRange(from, end);
					view.dispatch(tr);

					props.action(editor, {
						ai: this.options.ai,
						mathEditor: this.options.mathEditor,
					});
					view.focus();
				},
				items: ({ query }: { query: string }) => {
					const withFilteredCommands = MENU_OPTIONS.filter((group) => group.isEnabled).map((group) => ({
						...group,
						commands: group.commands.filter((item) => {
							const labelNormalized = item.label.toLowerCase().trim();
							const queryNormalized = query.toLowerCase().trim();

							if (item.aliases) {
								const aliases = item.aliases.map((alias) => alias.toLowerCase().trim());

								return labelNormalized.includes(queryNormalized) || aliases.includes(queryNormalized);
							}

							return labelNormalized.includes(queryNormalized);
						}),
					}));

					const withoutEmptyGroups = withFilteredCommands.filter((group) => {
						if (group.commands.length > 0) {
							return true;
						}

						return false;
					});

					const withEnabledSettings = withoutEmptyGroups.map((group) => ({
						...group,
						commands: group.commands.filter((command) => {
							const isEnabled =
								command.isEnabled && !(this.options.disabledCommands || []).includes(command.name);
							return isEnabled;
						}),
					}));

					return withEnabledSettings;
				},
				render: () => {
					let component: any;

					let scrollHandler: (() => void) | null = null;

					return {
						onStart: (props: SuggestionProps) => {
							component = new ReactRenderer(MenuList, {
								props,
								editor: props.editor,
							});

							const { view } = props.editor;
							const getReferenceClientRect = () => {
								if (!props.clientRect) {
									return props.editor.storage[extensionName].rect;
								}

								const rect = props.clientRect();

								if (!rect) {
									return props.editor.storage[extensionName].rect;
								}

								let yPos = rect.y;

								if (rect.top + component.element.offsetHeight + 40 > window.innerHeight) {
									const diff = rect.top + component.element.offsetHeight - window.innerHeight + 40;
									yPos = rect.y - diff;
								}

								return new DOMRect(rect.x, yPos, rect.width, rect.height);
							};

							scrollHandler = () => {
								popup?.[0].setProps({
									getReferenceClientRect,
								});
							};

							view.dom.parentElement?.addEventListener('scroll', scrollHandler);

							popup?.[0].setProps({
								getReferenceClientRect,
								appendTo: () => document.body,
								content: component.element,
							});

							popup?.[0].show();
						},

						onUpdate(props: SuggestionProps) {
							component.updateProps(props);

							const { view } = props.editor;
							const getReferenceClientRect = () => {
								if (!props.clientRect) {
									return props.editor.storage[extensionName].rect;
								}

								const rect = props.clientRect();

								if (!rect) {
									return props.editor.storage[extensionName].rect;
								}

								return new DOMRect(rect.x, rect.y, rect.width, rect.height);
							};

							const scrollHandler = () => {
								popup?.[0].setProps({
									getReferenceClientRect,
								});
							};

							view.dom.parentElement?.addEventListener('scroll', scrollHandler);

							props.editor.storage[extensionName].rect = props.clientRect
								? getReferenceClientRect()
								: {
										width: 0,
										height: 0,
										left: 0,
										top: 0,
										right: 0,
										bottom: 0,
									};
							popup?.[0].setProps({
								getReferenceClientRect,
							});
						},

						onKeyDown(props: SuggestionKeyDownProps) {
							if (props.event.key === 'Escape') {
								popup?.[0].hide();

								return true;
							}

							if (!popup?.[0].state.isShown) {
								popup?.[0].show();
							}

							return component.ref?.onKeyDown(props);
						},

						onExit(props) {
							popup?.[0].hide();
							if (scrollHandler) {
								const { view } = props.editor;
								view.dom.parentElement?.removeEventListener('scroll', scrollHandler);
							}
							component.destroy();
						},
					};
				},
			}),
		];
	},

	addStorage() {
		return {
			rect: {
				width: 0,
				height: 0,
				left: 0,
				top: 0,
				right: 0,
				bottom: 0,
			},
		};
	},
});

export default SlashCommand;
